import { FormattedMessage } from 'react-intl';
import ModalButton from '@/shared/components/ModalButton';
import Callout from '@/shared/components/Callout';
import { useCountries, type CountryOption } from '@catalog';
import { CreateAccountResponse } from '@/features/accounts/types';
import AccountSummaryCard from './CreationConfirmation/AccountSummaryCard';
import ScheduleCard from './CreationConfirmation/ScheduleCard';

interface Props {
  connection: CreateAccountResponse;
  onContinue: () => void;
}

const findCountryName = (code: string, options: CountryOption[]) =>
  options.find((country) => country.code === code)?.name ?? code;

export default function CreationConfirmation({ connection, onContinue }: Props) {
  const { data: countries } = useCountries();
  const { account, schedules } = connection;

  const originName = findCountryName(account.country_code, countries?.origin_countries ?? []);
  const destinationName = findCountryName(
    account.to_country_code,
    countries?.destination_countries ?? []
  );

  return (
    <div className="space-y-6">
      <Callout
        size="sm"
        icon={
          <svg className="h-5 w-5 shrink-0 text-accent-600" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path
              fillRule="evenodd"
              d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
              clipRule="evenodd"
            />
          </svg>
        }
      >
        <FormattedMessage id="accounts.create.confirmation.success" />
      </Callout>

      <AccountSummaryCard
        email={account.email}
        originName={originName}
        originCode={account.country_code}
        destinationName={destinationName}
        destinationCode={account.to_country_code}
      />

      <section className="space-y-4">
        <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
          <FormattedMessage
            id="accounts.create.confirmation.schedulesTitle"
            values={{ count: schedules.length }}
          />
        </p>

        {schedules.length > 0 ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {schedules.map((schedule) => (
              <ScheduleCard key={schedule.id} schedule={schedule} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500">
            <FormattedMessage id="accounts.create.confirmation.schedulesEmpty" />
          </p>
        )}
      </section>

      <div className="flex justify-end">
        <ModalButton onClick={onContinue}>
          <FormattedMessage id="accounts.create.confirmation.continue" />
        </ModalButton>
      </div>
    </div>
  );
}
